import {
  Box, Flex, Button, Input, Text, VStack,
  Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerFooter, DrawerCloseButton,
  useDisclosure, useBreakpointValue, Icon, SimpleGrid
} from "@chakra-ui/react";
import { FaSearch, FaBackspace } from "react-icons/fa";
import { Search } from "lucide-react";
import { ROTULO_SECAO, COR_ROTULO, SOMBRA } from "../VendaCardV2/ui";
import MultipleSelect from "../MultipleSelect";
import MultipleSelectMktplacesVendas from "../MultipleSelectMktplacesVendas";
import MultipleSelectFornecedoresVendas from "../MultipleSelectFornecedoresVendas";

const inputStyles = {
    bg: "white",
    borderRadius: "md",
    border: "1px solid",
    borderColor: "gray.200",
    fontSize: "13px",
    _hover: { borderColor: "gray.300" },
    _focus: { borderColor: "#F68633", boxShadow: "0 0 0 1px #F68633" },
};

function FilterField({ label, v2, children }) {
    return (
        <Box w="100%" textAlign={v2 ? "left" : undefined}>
            {v2 ? (
                <Text {...ROTULO_SECAO} color="gray.500" mb={1}>{label}</Text>
            ) : (
            <Text fontSize="10px" fontWeight="700" color="#F68633" textTransform="uppercase" mb={0.5} letterSpacing="0.3px">
                {label}
            </Text>
            )}
            {children}
        </Box>
    );
}

function FiltrosVendas({
    filtroBusca, handleChangeFiltroBusca,
    filtroDataInicial, handleChangeFiltroDataInicial,
    filtroDataFinal, handleChangeFiltroDataFinal,
    selectedOptionsStatus, handleSelectedOptionsStatusChange,
    selectedOptionsMktplaces, handleSelectedOptionsMktplaces,
    selectedOptionsStatusFornecedor, handleSelectedOptionsFornecedoresChange,
    handlePesquisar, limparFiltros, v2,
}) {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const isMobile = useBreakpointValue({ base: true, md: false });

    const handleSubmit = () => {
        handlePesquisar();
        if (isMobile) onClose();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSubmit();
    };

    const selectWrapper = { overflow: "visible", position: "relative" };

    const filterFields = (
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 6 }} spacing={3}>
            <FilterField label="Pesquisa" v2={v2}>
                <Input
                    placeholder="ID, produto, código ou cliente"
                    value={filtroBusca}
                    onChange={handleChangeFiltroBusca}
                    onKeyDown={handleKeyDown}
                    size="sm"
                    {...inputStyles}
                />
            </FilterField>

            <FilterField label="Status Venda" v2={v2}>
                <Box style={selectWrapper}>
                    <MultipleSelect
                        selectedOptionsStatus={selectedOptionsStatus}
                        handleSelectedOptionsStatusChange={handleSelectedOptionsStatusChange}
                        paginaVendas={true}
                    />
                </Box>
            </FilterField>

            <FilterField label="Origem" v2={v2}>
                <Box style={selectWrapper}>
                    <MultipleSelectMktplacesVendas
                        selectedOptionsStatus={selectedOptionsMktplaces}
                        handleSelectedOptionsStatusChange={handleSelectedOptionsMktplaces}
                        paginaVendas={true}
                    />
                </Box>
            </FilterField>

            <FilterField label="Fornecedores" v2={v2}>
                <Box style={selectWrapper}>
                    <MultipleSelectFornecedoresVendas
                        selectedOptionsStatus={selectedOptionsStatusFornecedor}
                        handleSelectedOptionsStatusChange={handleSelectedOptionsFornecedoresChange}
                        paginaVendas={true}
                    />
                </Box>
            </FilterField>

            <FilterField label="Data Inicial" v2={v2}>
                <Input
                    type="date"
                    value={filtroDataInicial}
                    onChange={handleChangeFiltroDataInicial}
                    onKeyDown={handleKeyDown}
                    size="sm"
                    {...inputStyles}
                />
            </FilterField>

            <FilterField label="Data Final" v2={v2}>
                <Input
                    type="date"
                    value={filtroDataFinal}
                    onChange={handleChangeFiltroDataFinal}
                    onKeyDown={handleKeyDown}
                    size="sm"
                    {...inputStyles}
                />
            </FilterField>
        </SimpleGrid>
    );

    const actionButtons = (
        <Flex gap={2} justify="center" mt={3}>
            <Button
                colorScheme="orange"
                size="sm"
                onClick={handleSubmit}
                leftIcon={<FaSearch />}
                borderRadius="md"
                px={5}
                fontWeight="700"
            >
                Filtrar
            </Button>
            <Button
                variant="outline"
                colorScheme="gray"
                size="sm"
                onClick={limparFiltros}
                leftIcon={<FaBackspace />}
                borderRadius="md"
                px={5}
                fontWeight="600"
            >
                Limpar
            </Button>
        </Flex>
    );

    // Mobile: drawer bottom sheet
    if (isMobile) {
        return (
            <Box w="100%" mx="auto" mt={2}>
                <Flex justify="center">
                    {v2 ? (
                        <Button onClick={onOpen} leftIcon={<Icon as={Search} boxSize="15px" />} w="100%" h="40px" bg="white" color="gray.700" border="1px solid"
                            borderColor="gray.200" borderRadius="xl" boxShadow={SOMBRA} fontSize="14px" fontWeight="600" _hover={{ bg: "gray.50" }} _active={{ bg: "gray.100" }}>
                            Buscar e filtrar
                        </Button>
                    ) : (
                    <Button
                        onClick={onOpen}
                        leftIcon={<Icon as={FaSearch} />}
                        colorScheme="orange"
                        variant="outline"
                        size="sm"
                        borderRadius="md"
                        fontWeight="600"
                    >
                        Buscar e Filtrar
                    </Button>
                    )}
                </Flex>

                <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
                    <DrawerOverlay />
                    <DrawerContent borderTopRadius="xl" maxH="85vh">
                        <DrawerCloseButton />
                        <DrawerHeader borderBottomWidth="1px" fontSize="md" color="gray.700" pb={3}>
                            <Flex align="center" gap={2}>
                                <Icon as={FaSearch} color="#F68633" boxSize="14px" />
                                Buscar e Filtrar
                            </Flex>
                        </DrawerHeader>
                        <DrawerBody py={4} overflowY="auto">
                            <VStack spacing={3} align="stretch">
                                {filterFields}
                            </VStack>
                        </DrawerBody>
                        <DrawerFooter borderTopWidth="1px" py={3}>
                            <Flex w="100%" gap={2}>
                                <Button flex={1} variant="outline" colorScheme="gray" onClick={() => { limparFiltros(); onClose(); }} leftIcon={<FaBackspace />} borderRadius="md">
                                    Limpar
                                </Button>
                                <Button flex={1} colorScheme="orange" onClick={handleSubmit} leftIcon={<FaSearch />} borderRadius="md">
                                    Filtrar
                                </Button>
                            </Flex>
                        </DrawerFooter>
                    </DrawerContent>
                </Drawer>
            </Box>
        );
    }

    // Desktop: inline dentro do container
    if (v2) {
        return (
            <Box w="100%" mt={2} px={4} py={3} bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" boxShadow={SOMBRA} overflow="visible">
                <Flex align="center" gap={1.5} color={COR_ROTULO} mb={2.5}>
                    <Icon as={Search} boxSize="13px" strokeWidth={2.25} />
                    <Text {...ROTULO_SECAO}>Filtros</Text>
                </Flex>
                {filterFields}
                {actionButtons}
            </Box>
        );
    }

    return (
        <Box w="100%" mx="auto" mt={2} border="1px solid #FF9F59" px={4} pt={3} pb={3} borderRadius="lg" boxShadow="md" bg="gray.50" overflow="visible">
            <Flex mt={-5} mb={1.5} px={1}>
                <Text
                    fontSize="8px"
                    color="#F68633"
                    fontWeight="bold"
                    bg="white"
                    w="50px"
                    px={1}
                    borderRadius="md"
                    border="1px solid #FF9F59"
                    textAlign="center"
                    lineHeight="14px"
                >
                    FILTROS
                </Text>
            </Flex>
            {filterFields}
            {actionButtons}
        </Box>
    );
}

export default FiltrosVendas;
